import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import apiService from "@/services/apiService";
import { POLICY_VERSIONS, readConsent } from "@/lib/consent";
import { recordCookieChoice } from "./CookieConsentBanner";

interface Latest {
  privacy: { version: string } | null;
  cookies: { version: string; choices: Record<string, boolean> | null } | null;
}

/**
 * After sign-in: staff must accept the current Privacy Policy version before
 * using the app (asked again whenever POLICY_VERSIONS.privacy changes), and a
 * cookie choice made before signing in is logged against their account.
 * Fails open: if the acceptance service cannot be reached, nobody is locked out.
 */
export default function PolicyAcceptanceGate() {
  const auth = useAuth() as any;
  const userId = auth?.user?.id;
  const signedIn = !!(auth?.token && userId);
  // Stand aside while the policies themselves are being read; the prompt
  // returns on any other page until the policy is accepted.
  const { pathname } = useLocation();
  const readingPolicy = pathname === "/privacy-policy" || pathname === "/cookie-policy";
  const [mustAccept, setMustAccept] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!signedIn) {
      setMustAccept(false);
      return;
    }
    let active = true;
    apiService
      .get<{ data: Latest }>("/policy-acceptances/me", undefined, false)
      .then((response: any) => {
        if (!active) return;
        const latest: Latest = response?.data ?? response;
        setMustAccept(latest?.privacy?.version !== POLICY_VERSIONS.privacy);
        const local = readConsent();
        const server = latest?.cookies;
        if (local && (server?.version !== local.version || server?.choices?.analytics !== local.choices.analytics)) {
          void recordCookieChoice(local.choices);
        }
      })
      .catch(() => {
        /* fail open */
      });
    return () => {
      active = false;
    };
  }, [signedIn, userId]);

  const accept = async () => {
    setSaving(true);
    setError("");
    try {
      await apiService.post("/policy-acceptances", { policy: "privacy", version: POLICY_VERSIONS.privacy }, false);
      setMustAccept(false);
      setAgreed(false);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Could not record your acceptance. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={mustAccept && !readingPolicy} onOpenChange={() => { /* must accept or sign out */ }}>
      <DialogContent
        className="max-w-lg"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> Privacy Policy
          </DialogTitle>
          <DialogDescription>
            Please read and accept our Privacy Policy (version {POLICY_VERSIONS.privacy}) to continue.
          </DialogDescription>
        </DialogHeader>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Patient records you work with belong to your hospital; we process them only on its instructions.</li>
          <li>Your account details, activity logs and audit trails are kept to run and secure the service.</li>
          <li>Analytics cookies are used only if you allow them, and never receive patient details.</li>
          <li>You can ask to access or correct your data, and withdraw consent at any time.</li>
        </ul>
        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-sm underline underline-offset-2">
          Read the full Privacy Policy (opens in a new tab)
        </a>
        <label className="flex items-start gap-2 text-sm">
          <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} className="mt-0.5" />
          <span>I have read and accept the Privacy Policy.</span>
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => auth?.logout?.()} disabled={saving}>Sign out</Button>
          <Button onClick={() => void accept()} disabled={!agreed || saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Accept and continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
